import {
    ActionFilter,
    FilterClear,
    FilterGroup,
    HeaderFilter,
    RoleFilter,
    useGetFilterPreferencesQuery,
    useOutsideClick,
    useUpdateFilterPreferences,
} from "modules/common";
import { PlatformFilter } from "modules/common/components/PlatformFilter";
import { mapToFilterName, UserActivityFilter } from "modules/common/utils/filters";
import React, { useEffect, useRef, useState } from "react";
import "./ActivityFilter.css"

interface Props {
    roleFilter: number[];
    platformFilter: number[];
    actionFilter: number[];
    setRoleFilter: React.Dispatch<React.SetStateAction<number[]>>;
    setPlatformFilter: React.Dispatch<React.SetStateAction<number[]>>;
    setActionFilter: React.Dispatch<React.SetStateAction<number[]>>;
    setCurrentPage: React.Dispatch<React.SetStateAction<number>>;
    searchFilter: string;
    setSearchFilter: React.Dispatch<React.SetStateAction<string>>;
}

export const ActivityFilters: React.FC<Props> = ({
    roleFilter,
    platformFilter,
    actionFilter,
    setRoleFilter,
    setPlatformFilter,
    setActionFilter,
    setCurrentPage,
    searchFilter,
    setSearchFilter,
}) => {
    // dropdown states
    const [showRoleFilter, setShowRoleFilter] = useState(false);
    const [showPlatformFilter, setShowPlatformFilter] = useState(false);
    const [showActionFilter, setShowActionFilter] = useState(false);

    // refs
    const roleFilterRef = useRef<HTMLDivElement>(null);
    const platformFilterRef = useRef<HTMLDivElement>(null);
    const actionFilterRef = useRef<HTMLDivElement>(null);

    // outside click
    useOutsideClick(roleFilterRef, () => setShowRoleFilter(false));
    useOutsideClick(platformFilterRef, () => setShowPlatformFilter(false));
    useOutsideClick(actionFilterRef, () => setShowActionFilter(false));

    // handle filter change/clear
    const handleFilterChange = (value: number, filterName: string) => {
        updateFilterPreferencesMutation.mutate({
            filterName,
            filterIntValue: value,
        });
        setCurrentPage(1);
    };
    const handleFilterClear = (filterName: string) => {
        updateFilterPreferencesMutation.mutate({
            filterName,
            clearFilter: true,
        });
        setCurrentPage(1);
    };
    const handleClearFilters = () => {
        setSearchFilter("");
        handleFilterClear(ROLE_FILTER_NAME);
        handleFilterClear(PLATFORM_FILTER_NAME);
        handleFilterClear(ACTION_FILTER_NAME);
    };

    const updateFilterPreferencesMutation = useUpdateFilterPreferences();

    // query
    const filterPreferencesQuery = useGetFilterPreferencesQuery([
        ROLE_FILTER_NAME,
        PLATFORM_FILTER_NAME,
        ACTION_FILTER_NAME,
    ]);

    useEffect(() => {
        if (filterPreferencesQuery.some(q => q.isLoading || q.isFetching)) return;

        const role = filterPreferencesQuery.find(fp => fp.data?.filterName === ROLE_FILTER_NAME)
            ?.data?.filterIntValues;
        const platform = filterPreferencesQuery.find(
            fp => fp.data?.filterName === PLATFORM_FILTER_NAME
        )?.data?.filterIntValues;
        const action = filterPreferencesQuery.find(fp => fp.data?.filterName === ACTION_FILTER_NAME)
            ?.data?.filterIntValues;

        if (role) setRoleFilter(role);

        if (platform) setPlatformFilter(platform);

        if (action) setActionFilter(action);
    }, [filterPreferencesQuery]);

    return (
        <HeaderFilter name="user-activity-filter" title="AKTIVNOST KORISNIKA">
            <FilterGroup>
                <div ref={roleFilterRef}>
                    <RoleFilter
                        onChange={e =>
                            handleFilterChange(parseInt(e.target.value), ROLE_FILTER_NAME)
                        }
                        onClear={() => handleFilterClear(ROLE_FILTER_NAME)}
                        onClick={() => setShowRoleFilter(prev => !prev)}
                        value={roleFilter}
                        isActive={roleFilter.length > 0}
                        showDropdown={showRoleFilter}
                    />
                </div>

                <div ref={platformFilterRef}>
                    <PlatformFilter
                        onChange={e =>
                            handleFilterChange(parseInt(e.target.value), PLATFORM_FILTER_NAME)
                        }
                        onClear={() => handleFilterClear(PLATFORM_FILTER_NAME)}
                        onClick={() => setShowPlatformFilter(prev => !prev)}
                        value={platformFilter}
                        isActive={platformFilter.length > 0}
                        showDropdown={showPlatformFilter}
                    />
                </div>

                <div ref={actionFilterRef}>
                    <ActionFilter
                        onChange={e =>
                            handleFilterChange(parseInt(e.target.value), ACTION_FILTER_NAME)
                        }
                        onClear={() => handleFilterClear(ACTION_FILTER_NAME)}
                        onClick={() => setShowActionFilter(prev => !prev)}
                        value={actionFilter}
                        isActive={actionFilter.length > 0}
                        showDropdown={showActionFilter}
                    />
                </div>

                <FilterClear
                    show={Boolean(
                        searchFilter.length ||
                            roleFilter.length ||
                            platformFilter.length ||
                            actionFilter.length
                    )}
                    onClearFilter={handleClearFilters}
                />
            </FilterGroup>
        </HeaderFilter>
    );
};

// map filter names
const ROLE_FILTER_NAME = mapToFilterName<UserActivityFilter>(ActivityFilters.name, "roleFilter");
const PLATFORM_FILTER_NAME = mapToFilterName<UserActivityFilter>(
    ActivityFilters.name,
    "platformFilter"
);
const ACTION_FILTER_NAME = mapToFilterName<UserActivityFilter>(
    ActivityFilters.name,
    "actionFilter"
);
